import type { AutomationTypeKey } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export const AUTOMATIONS_ROUTES = { LIST: '/marketing/automacoes' } as const;

const ENDPOINT = '/api/v1/automations';

export interface Automation {
  type: AutomationTypeKey;
  label: string;
  hint: string;
  enabled: boolean;
  body: string;
  couponId: string | null;
  couponCode: string | null;
  hoursBefore: number | null;
  sendWindowStart: string | null;
  sendWindowEnd: string | null;
}

export type AutomationInput = Partial<
  Pick<
    Automation,
    'enabled' | 'body' | 'couponId' | 'hoursBefore' | 'sendWindowStart' | 'sendWindowEnd'
  >
>;

export function useAutomations() {
  return useQuery({
    queryKey: ['automations'],
    queryFn: () => apiClient.get<{ data: Automation[] }>(ENDPOINT),
  });
}

export function useUpdateAutomation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ type, input }: { type: AutomationTypeKey; input: AutomationInput }) =>
      apiClient.patch<Automation>(`${ENDPOINT}/${type}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['automations'] }),
  });
}

export function useTestAutomation() {
  return useMutation({
    mutationFn: (type: AutomationTypeKey) =>
      apiClient.post<{ sent: boolean; to: string }>(`${ENDPOINT}/${type}/test`, {}),
  });
}
